import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Wallet, ArrowUpCircle, ArrowDownCircle, RefreshCw, Plus,
  ExternalLink, Copy, CheckCircle, AlertCircle, Clock, Shield,
  DollarSign, Landmark, ChevronDown, ChevronRight, X
} from 'lucide-react';
import { useUser } from '../contexts/UserContext';
import { cryptoApi, bankApi, CryptoWallet, CryptoBalance, RampTransaction, LedgerEntry } from '../services/api';
import { TENANT } from '../config';
import toast from 'react-hot-toast';
import { PortalContainer } from '../components/portal/PortalContainer';

// ─── Types ───────────────────────────────────────────────
interface BankAccount {
  id: string;
  institution_name: string;
  account_name: string;
  account_type: string;
  last4: string;
  is_active: boolean;
}

// ─── Constants ───────────────────────────────────────────
const SUPPORTED_NETWORKS = [
  { id: 'base', name: 'Base', icon: '🔵', color: 'blue' },
  { id: 'polygon', name: 'Polygon', icon: '🟣', color: 'purple' },
  { id: 'arbitrum', name: 'Arbitrum', icon: '🔷', color: 'cyan' },
  { id: 'ethereum', name: 'Ethereum', icon: '⟠', color: 'indigo' },
  { id: 'optimism', name: 'Optimism', icon: '🔴', color: 'red' },
];

const STATUS_COLORS: Record<string, string> = {
  pending: 'bg-yellow-500/15 text-yellow-700 border-yellow-500/30',
  awaiting_payment: 'bg-orange-500/15 text-orange-700 border-orange-500/30',
  processing: 'bg-blue-500/15 text-blue-700 border-blue-500/30',
  confirming: 'bg-cyan-500/15 text-cyan-700 border-cyan-500/30',
  completed: 'bg-emerald-500/15 text-emerald-700 border-emerald-500/30',
  failed: 'bg-red-500/15 text-red-700 border-red-500/30',
  cancelled: 'bg-muted text-muted-foreground border-border',
  refunded: 'bg-purple-500/15 text-purple-700 border-purple-500/30',
};

// ─── Utility ─────────────────────────────────────────────
const formatUsd = (cents: number) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(cents / 100);

const formatUsdDecimal = (amount: number) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount);

const shortenAddress = (addr: string) =>
  addr ? `${addr.slice(0, 6)}...${addr.slice(-4)}` : '';

const formatDate = (iso: string) =>
  new Date(iso).toLocaleDateString('en-US', {
    month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit',
  });

// ─── Main Page ───────────────────────────────────────────
export default function CryptoFunding() {
  const { user } = useUser();

  // Data state
  const [wallets, setWallets] = useState<CryptoWallet[]>([]);
  const [balances, setBalances] = useState<CryptoBalance[]>([]);
  const [transactions, setTransactions] = useState<RampTransaction[]>([]);
  const [ledger, setLedger] = useState<LedgerEntry[]>([]);
  const [bankAccounts, setBankAccounts] = useState<BankAccount[]>([]);

  // UI state
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<'overview' | 'buy' | 'sell' | 'wallets' | 'history'>('overview');
  const [refreshing, setRefreshing] = useState(false);

  // Modal state
  const [showCreateWallet, setShowCreateWallet] = useState(false);
  const [showRegisterWallet, setShowRegisterWallet] = useState(false);

  // ─── Data Loading ──────────────────────────────
  const loadData = useCallback(async () => {
    try {
      const [walletsRes, balancesRes, txRes, ledgerRes, bankRes] = await Promise.all([
        cryptoApi.getWallets(),
        cryptoApi.getBalances(),
        cryptoApi.getTransactions({ limit: 20 }),
        cryptoApi.getLedgerHistory({ limit: 20 }),
        bankApi.getAccounts().then(r => r.data).catch(() => ({ success: false, data: { accounts: [] } })),
      ]);

      if (walletsRes.success && walletsRes.data) setWallets(walletsRes.data);
      if (balancesRes.success && balancesRes.data) setBalances(balancesRes.data);
      if (txRes.success && txRes.data) setTransactions(txRes.data);
      if (ledgerRes.success && ledgerRes.data) setLedger(ledgerRes.data);
      if (bankRes?.success && (bankRes as any)?.data?.accounts) {
        setBankAccounts((bankRes as any).data.accounts);
      }
    } catch (err) {
      console.error('Failed to load crypto data:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  const handleRefresh = async () => {
    setRefreshing(true);
    await loadData();
    setRefreshing(false);
    toast.success('Data refreshed');
  };

  // ─── Wallet helpers ────────────────────────────
  const primaryWallet = wallets.find(w => w.is_primary && w.is_active);
  const totalUsdcBalance = balances.reduce((sum, b) => sum + (b.balance_display || 0), 0);

  const copyAddress = (addr: string) => {
    navigator.clipboard.writeText(addr);
    toast.success('Address copied');
  };

  // ─── Loading ───────────────────────────────────
  if (loading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <RefreshCw className="w-8 h-8 text-muted-foreground animate-spin" />
      </div>
    );
  }

  // ─── Tabs ──────────────────────────────────────
  const tabs = [
    { id: 'overview' as const, label: 'Overview', icon: <Wallet className="w-4 h-4" /> },
    { id: 'buy' as const, label: 'Buy USDC', icon: <ArrowDownCircle className="w-4 h-4" /> },
    { id: 'sell' as const, label: 'Sell USDC', icon: <ArrowUpCircle className="w-4 h-4" /> },
    { id: 'wallets' as const, label: 'Wallets', icon: <Shield className="w-4 h-4" /> },
    { id: 'history' as const, label: 'History', icon: <Clock className="w-4 h-4" /> },
  ];

  return (
    <PortalContainer className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-foreground">Crypto Funding</h1>
            <p className="text-muted-foreground text-sm mt-1">
              Buy & sell USDC with your linked bank account
            </p>
          </div>
          <button
            onClick={handleRefresh}
            disabled={refreshing}
            className="p-2.5 rounded-xl bg-muted/40 hover:bg-muted/70 border border-border text-foreground transition-colors"
          >
            <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
          </button>
        </div>

        {/* Balance Banner */}
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-card border border-border rounded-2xl p-6"
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-muted-foreground text-sm font-medium mb-1">Total USDC Balance</p>
              <p className="text-3xl font-bold text-foreground">
                {formatUsdDecimal(totalUsdcBalance)}
              </p>
              {primaryWallet && (
                <div className="flex items-center gap-2 mt-2">
                  <span className="text-muted-foreground text-xs font-mono">
                    {shortenAddress(primaryWallet.wallet_address)}
                  </span>
                  <button
                    onClick={() => copyAddress(primaryWallet.wallet_address)}
                    className="text-muted-foreground hover:text-foreground transition-colors"
                  >
                    <Copy className="w-3 h-3" />
                  </button>
                </div>
              )}
            </div>
            <div className="flex gap-3">
              <button
                onClick={() => setActiveTab('buy')}
                className="px-5 py-2.5 rounded-xl bg-primary hover:bg-primary/90 text-primary-foreground font-medium text-sm transition-colors"
              >
                Buy
              </button>
              <button
                onClick={() => setActiveTab('sell')}
                className="px-5 py-2.5 rounded-xl bg-muted/40 hover:bg-muted/70 border border-border text-foreground font-medium text-sm transition-colors"
              >
                Sell
              </button>
            </div>
          </div>
        </motion.div>

        {/* Tab Bar */}
        <div className="flex gap-1 bg-muted/40 border border-border rounded-xl p-1 overflow-x-auto">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium whitespace-nowrap transition-all ${
                activeTab === tab.id
                  ? 'bg-card text-foreground border border-border shadow-sm'
                  : 'text-muted-foreground hover:text-foreground hover:bg-muted/50'
              }`}
            >
              {tab.icon}
              {tab.label}
            </button>
          ))}
        </div>

        {/* Tab Content */}
        <AnimatePresence mode="wait">
          {activeTab === 'overview' && (
            <OverviewTab
              key="overview"
              wallets={wallets}
              balances={balances}
              transactions={transactions}
              onSetTab={setActiveTab}
            />
          )}
          {activeTab === 'buy' && (
            <RampTab
              key="buy"
              direction="onramp"
              wallets={wallets}
              bankAccounts={bankAccounts}
              onSuccess={loadData}
            />
          )}
          {activeTab === 'sell' && (
            <RampTab
              key="sell"
              direction="offramp"
              wallets={wallets}
              bankAccounts={bankAccounts}
              onSuccess={loadData}
            />
          )}
          {activeTab === 'wallets' && (
            <WalletsTab
              key="wallets"
              wallets={wallets}
              onRefresh={loadData}
              showCreateWallet={showCreateWallet}
              setShowCreateWallet={setShowCreateWallet}
              showRegisterWallet={showRegisterWallet}
              setShowRegisterWallet={setShowRegisterWallet}
            />
          )}
          {activeTab === 'history' && (
            <HistoryTab
              key="history"
              transactions={transactions}
              ledger={ledger}
            />
          )}
        </AnimatePresence>
    </PortalContainer>
  );
}


// ═════════════════════════════════════════════════════════════════════════════
// OVERVIEW TAB
// ═════════════════════════════════════════════════════════════════════════════

function OverviewTab({
  wallets,
  balances,
  transactions,
  onSetTab,
}: {
  wallets: CryptoWallet[];
  balances: CryptoBalance[];
  transactions: RampTransaction[];
  onSetTab: (tab: 'overview' | 'buy' | 'sell' | 'wallets' | 'history') => void;
}) {
  const recentTx = transactions.slice(0, 5);

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -10 }}
      className="space-y-6"
    >
      {/* Quick Actions */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {[
          { label: 'Buy USDC', icon: <ArrowDownCircle className="w-5 h-5" />, tab: 'buy' as const },
          { label: 'Sell USDC', icon: <ArrowUpCircle className="w-5 h-5" />, tab: 'sell' as const },
          { label: 'My Wallets', icon: <Wallet className="w-5 h-5" />, tab: 'wallets' as const },
          { label: 'History', icon: <Clock className="w-5 h-5" />, tab: 'history' as const },
        ].map((action) => (
          <button
            key={action.label}
            onClick={() => onSetTab(action.tab)}
            className="bg-card border border-border rounded-xl p-4 text-left transition-colors hover:bg-muted/30"
          >
            <div className="text-primary mb-2">{action.icon}</div>
            <span className="text-foreground font-medium text-sm">{action.label}</span>
          </button>
        ))}
      </div>

      {/* Wallet Summary */}
      {wallets.length === 0 ? (
        <div className="bg-card border border-border rounded-2xl p-8 text-center">
          <Wallet className="w-12 h-12 text-muted-foreground mx-auto mb-3" />
          <h3 className="text-foreground font-semibold text-lg mb-2">No Wallets Yet</h3>
          <p className="text-muted-foreground mb-4 text-sm">
            Create a self-custody wallet to start buying and selling USDC.
          </p>
          <button
            onClick={() => onSetTab('wallets')}
            className="px-6 py-2.5 rounded-xl bg-primary hover:bg-primary/90 text-primary-foreground font-medium text-sm transition-colors"
          >
            Create Wallet
          </button>
        </div>
      ) : (
        <div className="bg-card border border-border rounded-2xl p-5">
          <h3 className="text-foreground font-semibold mb-3 flex items-center gap-2">
            <Shield className="w-4 h-4 text-primary" />
            Wallets ({wallets.length})
          </h3>
          <div className="space-y-2">
            {wallets.slice(0, 3).map((w) => (
              <div key={w.id} className="flex items-center justify-between py-2 px-3 rounded-lg bg-muted/30 border border-border/60">
                <div className="flex items-center gap-3">
                  <span className="text-lg">{SUPPORTED_NETWORKS.find(n => n.id === w.network)?.icon || '🔗'}</span>
                  <div>
                    <span className="text-foreground text-sm font-mono">{shortenAddress(w.wallet_address)}</span>
                    <span className="text-muted-foreground text-xs ml-2 capitalize">{w.wallet_type}</span>
                  </div>
                </div>
                <span className="text-muted-foreground text-sm">
                  {w.is_primary && <span className="text-primary text-xs mr-2">Primary</span>}
                  {w.network}
                </span>
              </div>
            ))}
            {wallets.length > 3 && (
              <button onClick={() => onSetTab('wallets')} className="text-primary text-sm hover:opacity-80 transition-colors">
                View all {wallets.length} wallets →
              </button>
            )}
          </div>
        </div>
      )}

      {/* Recent Transactions */}
      {recentTx.length > 0 && (
        <div className="bg-card border border-border rounded-2xl p-5">
          <h3 className="text-foreground font-semibold mb-3 flex items-center gap-2">
            <Clock className="w-4 h-4 text-primary" />
            Recent Activity
          </h3>
          <div className="space-y-2">
            {recentTx.map((tx) => (
              <div key={tx.id} className="flex items-center justify-between py-2 px-3 rounded-lg bg-muted/30 border border-border/60">
                <div className="flex items-center gap-3">
                  {tx.direction === 'onramp' ? (
                    <ArrowDownCircle className="w-5 h-5 text-primary" />
                  ) : (
                    <ArrowUpCircle className="w-5 h-5 text-primary" />
                  )}
                  <div>
                    <span className="text-foreground text-sm">
                      {tx.direction === 'onramp' ? 'Buy' : 'Sell'} USDC
                    </span>
                    <span className="text-muted-foreground text-xs ml-2">{formatDate(tx.created_at)}</span>
                  </div>
                </div>
                <div className="text-right">
                  <span className="text-foreground text-sm font-medium">{formatUsd(tx.fiat_amount)}</span>
                  <span className={`ml-2 text-xs px-2 py-0.5 rounded-full border ${STATUS_COLORS[tx.status] || STATUS_COLORS.pending}`}>
                    {tx.status}
                  </span>
                </div>
              </div>
            ))}
          </div>
          <button
            onClick={() => onSetTab('history')}
            className="w-full mt-3 text-center text-primary text-sm hover:opacity-80 transition-colors"
          >
            View all transactions →
          </button>
        </div>
      )}
    </motion.div>
  );
}


// ═════════════════════════════════════════════════════════════════════════════
// RAMP TAB (Buy/Sell)
// ═════════════════════════════════════════════════════════════════════════════

function RampTab({
  direction,
  wallets,
  bankAccounts,
  onSuccess,
}: {
  direction: 'onramp' | 'offramp';
  wallets: CryptoWallet[];
  bankAccounts: BankAccount[];
  onSuccess: () => void;
}) {
  const isBuy = direction === 'onramp';

  const [amount, setAmount] = useState('');
  const [network, setNetwork] = useState('base');
  const [selectedBank, setSelectedBank] = useState('');
  const [selectedWallet, setSelectedWallet] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [showNetworkSelect, setShowNetworkSelect] = useState(false);

  // Auto-select first bank and wallet
  useEffect(() => {
    if (bankAccounts.length > 0 && !selectedBank) {
      setSelectedBank(bankAccounts[0].id);
    }
    const primary = wallets.find(w => w.is_primary && w.is_active);
    if (primary && !selectedWallet) {
      setSelectedWallet(primary.wallet_address);
    }
  }, [bankAccounts, wallets, selectedBank, selectedWallet]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const amountUsd = parseFloat(amount);
    if (!amountUsd || amountUsd <= 0) {
      toast.error('Enter a valid amount');
      return;
    }
    if (!selectedBank) {
      toast.error('Select a bank account');
      return;
    }

    setSubmitting(true);
    try {
      const params: any = {
        amountUsd,
        network,
        externalAccountId: selectedBank,
      };
      if (isBuy && selectedWallet) {
        params.userWalletAddress = selectedWallet;
      }

      const res = isBuy
        ? await cryptoApi.initiateOnramp(params)
        : await cryptoApi.initiateOfframp(params);

      if (res.success) {
        toast.success(
          isBuy
            ? `Buying $${amountUsd} of USDC. ACH transfer initiated.`
            : `Selling $${amountUsd} of USDC. Payout initiated.`
        );
        setAmount('');
        onSuccess();
      } else {
        toast.error(res.error || res.message || 'Transaction failed');
      }
    } catch (err: any) {
      toast.error(err.message || 'Something went wrong');
    } finally {
      setSubmitting(false);
    }
  };

  const presetAmounts = [25, 50, 100, 250, 500, 1000];

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -10 }}
    >
      <div className="bg-card border border-border rounded-2xl p-6">
        <h3 className="text-xl font-semibold text-foreground mb-1 flex items-center gap-2">
          {isBuy ? <ArrowDownCircle className="w-5 h-5 text-primary" /> : <ArrowUpCircle className="w-5 h-5 text-primary" />}
          {isBuy ? 'Buy USDC' : 'Sell USDC'}
        </h3>
        <p className="text-muted-foreground text-sm mb-6">
          {isBuy
            ? 'Purchase USDC via ACH from your linked bank account'
            : 'Convert your USDC balance to cash via ACH payout'}
        </p>

        <form onSubmit={handleSubmit} className="space-y-5">
          {/* Amount Input */}
          <div>
            <label className="text-muted-foreground text-sm font-medium mb-2 block">Amount (USD)</label>
            <div className="relative">
              <DollarSign className="absolute left-3.5 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
              <input
                type="number"
                min="10"
                step="0.01"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                placeholder="0.00"
                className="w-full pl-10 pr-4 py-3.5 rounded-xl bg-background border border-input text-foreground text-lg font-medium placeholder:text-muted-foreground/60 focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-colors"
              />
            </div>
            {/* Presets */}
            <div className="flex flex-wrap gap-2 mt-3">
              {presetAmounts.map((p) => (
                <button
                  key={p}
                  type="button"
                  onClick={() => setAmount(String(p))}
                  className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all border ${
                    amount === String(p)
                      ? 'bg-primary/20 text-foreground border-primary'
                      : 'bg-muted/40 text-muted-foreground border-border hover:bg-muted/70 hover:text-foreground'
                  }`}
                >
                  ${p}
                </button>
              ))}
            </div>
          </div>

          {/* Network Selector */}
          <div>
            <label className="text-muted-foreground text-sm font-medium mb-2 block">Network</label>
            <div className="relative">
              <button
                type="button"
                onClick={() => setShowNetworkSelect(!showNetworkSelect)}
                className="w-full flex items-center justify-between px-4 py-3 rounded-xl bg-background border border-input text-foreground hover:bg-muted/30 transition-colors"
              >
                <span className="flex items-center gap-2">
                  {SUPPORTED_NETWORKS.find(n => n.id === network)?.icon}
                  {SUPPORTED_NETWORKS.find(n => n.id === network)?.name || network}
                </span>
                <ChevronDown className="w-4 h-4 text-muted-foreground" />
              </button>
              {showNetworkSelect && (
                <div className="absolute z-10 w-full mt-1 bg-card border border-border rounded-xl py-1 shadow-lg">
                  {SUPPORTED_NETWORKS.map((n) => (
                    <button
                      key={n.id}
                      type="button"
                      onClick={() => { setNetwork(n.id); setShowNetworkSelect(false); }}
                      className={`w-full px-4 py-2.5 text-left flex items-center gap-2 hover:bg-muted/40 transition-colors ${
                        network === n.id ? 'text-primary' : 'text-foreground'
                      }`}
                    >
                      <span>{n.icon}</span>
                      <span>{n.name}</span>
                      {network === n.id && <CheckCircle className="w-4 h-4 ml-auto" />}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Bank Account Selector */}
          <div>
            <label className="text-muted-foreground text-sm font-medium mb-2 block">
              <Landmark className="w-4 h-4 inline mr-1" />
              Bank Account
            </label>
            {bankAccounts.length === 0 ? (
              <div className="px-4 py-3 rounded-xl bg-muted/30 border border-border text-muted-foreground text-sm">
                <AlertCircle className="w-4 h-4 inline mr-1" />
                No bank accounts linked. Go to Banking to link one first.
              </div>
            ) : (
              <select
                value={selectedBank}
                onChange={(e) => setSelectedBank(e.target.value)}
                className="w-full px-4 py-3 rounded-xl bg-background border border-input text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary"
              >
                {bankAccounts.map((ba) => (
                  <option key={ba.id} value={ba.id}>
                    {ba.institution_name} ····{ba.last4} ({ba.account_type})
                  </option>
                ))}
              </select>
            )}
          </div>

          {/* Wallet Selector (Buy only) */}
          {isBuy && wallets.length > 0 && (
            <div>
              <label className="text-muted-foreground text-sm font-medium mb-2 block">
                <Wallet className="w-4 h-4 inline mr-1" />
                Receive To
              </label>
              <select
                value={selectedWallet}
                onChange={(e) => setSelectedWallet(e.target.value)}
                className="w-full px-4 py-3 rounded-xl bg-background border border-input text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary"
              >
                {wallets.filter(w => w.is_active).map((w) => (
                  <option key={w.id} value={w.wallet_address}>
                    {shortenAddress(w.wallet_address)} ({w.network}){w.is_primary ? ' ★ Primary' : ''}
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* Summary */}
          {amount && parseFloat(amount) > 0 && (
            <div className="bg-muted/30 rounded-xl p-4 space-y-2 border border-border">
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">{isBuy ? 'You pay' : 'You sell'}</span>
                <span className="text-foreground font-medium">{formatUsdDecimal(parseFloat(amount))}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Network</span>
                <span className="text-foreground">{SUPPORTED_NETWORKS.find(n => n.id === network)?.name}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Estimated fee</span>
                <span className="text-muted-foreground">~{formatUsdDecimal(parseFloat(amount) * 0.005)}</span>
              </div>
              <hr className="border-border" />
              <div className="flex justify-between text-sm font-medium">
                <span className="text-foreground">{isBuy ? 'You receive (est.)' : 'You get (est.)'}</span>
                <span className="text-foreground">
                  {isBuy
                    ? `~${(parseFloat(amount) * 0.995).toFixed(2)} USDC`
                    : formatUsdDecimal(parseFloat(amount) * 0.995)}
                </span>
              </div>
            </div>
          )}

          {/* Submit */}
          <button
            type="submit"
            disabled={submitting || !amount || !selectedBank || bankAccounts.length === 0}
            className="w-full py-3.5 rounded-xl font-semibold bg-primary text-primary-foreground hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {submitting ? (
              <RefreshCw className="w-5 h-5 animate-spin inline mr-2" />
            ) : null}
            {submitting ? 'Processing...' : isBuy ? `Buy $${amount || '0'} USDC` : `Sell $${amount || '0'} USDC`}
          </button>
        </form>
      </div>
    </motion.div>
  );
}


// ═════════════════════════════════════════════════════════════════════════════
// WALLETS TAB
// ═════════════════════════════════════════════════════════════════════════════

function WalletsTab({
  wallets,
  onRefresh,
  showCreateWallet,
  setShowCreateWallet,
  showRegisterWallet,
  setShowRegisterWallet,
}: {
  wallets: CryptoWallet[];
  onRefresh: () => void;
  showCreateWallet: boolean;
  setShowCreateWallet: (v: boolean) => void;
  showRegisterWallet: boolean;
  setShowRegisterWallet: (v: boolean) => void;
}) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -10 }}
      className="space-y-5"
    >
      {/* Actions */}
      <div className="flex gap-3">
        <button
          onClick={() => setShowCreateWallet(true)}
          className="flex items-center gap-2 px-5 py-2.5 rounded-xl bg-primary hover:bg-primary/90 text-primary-foreground font-medium text-sm transition-colors"
        >
          <Plus className="w-4 h-4" />
          Create Wallet
        </button>
        <button
          onClick={() => setShowRegisterWallet(true)}
          className="flex items-center gap-2 px-5 py-2.5 rounded-xl bg-muted/40 hover:bg-muted/70 text-foreground font-medium text-sm border border-border transition-colors"
        >
          <ExternalLink className="w-4 h-4" />
          Link External
        </button>
      </div>

      {/* Wallet List */}
      {wallets.length === 0 ? (
        <div className="bg-card border border-border rounded-2xl p-8 text-center">
          <Shield className="w-12 h-12 text-muted-foreground mx-auto mb-3" />
          <h3 className="text-foreground font-semibold text-lg mb-2">No Wallets</h3>
          <p className="text-muted-foreground text-sm">Create a self-custody wallet on your preferred network.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {wallets.map((w, idx) => (
            <motion.div
              key={w.id}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: idx * 0.05 }}
              className="bg-card border border-border rounded-xl p-4 hover:bg-muted/30 transition-colors"
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <span className="text-2xl">
                    {SUPPORTED_NETWORKS.find(n => n.id === w.network)?.icon || '🔗'}
                  </span>
                  <div>
                    <div className="flex items-center gap-2">
                      <span className="text-foreground font-mono text-sm">{shortenAddress(w.wallet_address)}</span>
                      <button
                        onClick={() => { navigator.clipboard.writeText(w.wallet_address); toast.success('Copied'); }}
                        className="text-muted-foreground hover:text-foreground transition-colors"
                      >
                        <Copy className="w-3.5 h-3.5" />
                      </button>
                      {w.is_primary && (
                        <span className="text-xs bg-primary/20 text-foreground px-2 py-0.5 rounded-full border border-primary">
                          Primary
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-2 mt-1">
                      <span className="text-muted-foreground text-xs capitalize">{w.wallet_type}</span>
                      <span className="text-muted-foreground/60 text-xs">·</span>
                      <span className="text-muted-foreground text-xs capitalize">{w.network}</span>
                      <span className="text-muted-foreground/60 text-xs">·</span>
                      <span className="text-muted-foreground text-xs capitalize">{w.purpose}</span>
                    </div>
                  </div>
                </div>
                <div className="text-right">
                  {w.balance_usdc && (
                    <span className="text-foreground font-medium text-sm">{parseFloat(w.balance_usdc).toFixed(2)} USDC</span>
                  )}
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      )}

      {/* Create Wallet Modal */}
      {showCreateWallet && (
        <CreateWalletModal
          onClose={() => setShowCreateWallet(false)}
          onSuccess={() => { setShowCreateWallet(false); onRefresh(); }}
        />
      )}

      {/* Register External Wallet Modal */}
      {showRegisterWallet && (
        <RegisterWalletModal
          onClose={() => setShowRegisterWallet(false)}
          onSuccess={() => { setShowRegisterWallet(false); onRefresh(); }}
        />
      )}
    </motion.div>
  );
}


// ═════════════════════════════════════════════════════════════════════════════
// HISTORY TAB
// ═════════════════════════════════════════════════════════════════════════════

function HistoryTab({
  transactions,
  ledger,
}: {
  transactions: RampTransaction[];
  ledger: LedgerEntry[];
}) {
  const [historyTab, setHistoryTab] = useState<'transactions' | 'ledger'>('transactions');

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -10 }}
    >
      {/* Sub-tabs */}
      <div className="flex gap-1 bg-muted/40 border border-border rounded-xl p-1 mb-4">
        <button
          onClick={() => setHistoryTab('transactions')}
          className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
            historyTab === 'transactions'
              ? 'bg-card text-foreground border border-border shadow-sm'
              : 'text-muted-foreground hover:text-foreground hover:bg-muted/50'
          }`}
        >
          Transactions
        </button>
        <button
          onClick={() => setHistoryTab('ledger')}
          className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
            historyTab === 'ledger'
              ? 'bg-card text-foreground border border-border shadow-sm'
              : 'text-muted-foreground hover:text-foreground hover:bg-muted/50'
          }`}
        >
          Ledger
        </button>
      </div>

      {historyTab === 'transactions' ? (
        <div className="bg-card border border-border rounded-2xl overflow-hidden">
          {transactions.length === 0 ? (
            <div className="p-8 text-center text-muted-foreground">No transactions yet.</div>
          ) : (
            <div className="divide-y divide-border">
              {transactions.map((tx) => (
                <div key={tx.id} className="p-4 hover:bg-muted/30 transition-colors">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      {tx.direction === 'onramp' ? (
                        <div className="w-9 h-9 rounded-full bg-primary/20 flex items-center justify-center">
                          <ArrowDownCircle className="w-5 h-5 text-primary" />
                        </div>
                      ) : (
                        <div className="w-9 h-9 rounded-full bg-primary/20 flex items-center justify-center">
                          <ArrowUpCircle className="w-5 h-5 text-primary" />
                        </div>
                      )}
                      <div>
                        <p className="text-foreground text-sm font-medium">
                          {tx.direction === 'onramp' ? 'Bought' : 'Sold'} USDC
                        </p>
                        <p className="text-muted-foreground text-xs">{formatDate(tx.created_at)}</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-foreground text-sm font-medium">{formatUsd(tx.fiat_amount)}</p>
                      <span className={`text-xs px-2 py-0.5 rounded-full border ${STATUS_COLORS[tx.status] || STATUS_COLORS.pending}`}>
                        {tx.status}
                      </span>
                    </div>
                  </div>
                  {tx.blockchain_tx_hash && (
                    <div className="mt-2 pl-12">
                      <span className="text-muted-foreground text-xs font-mono">
                        tx: {shortenAddress(tx.blockchain_tx_hash)}
                      </span>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      ) : (
        <div className="bg-card border border-border rounded-2xl overflow-hidden">
          {ledger.length === 0 ? (
            <div className="p-8 text-center text-muted-foreground">No ledger entries yet.</div>
          ) : (
            <div className="divide-y divide-border">
              {ledger.map((entry) => (
                <div key={entry.id} className="p-4 hover:bg-muted/30 transition-colors">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-foreground text-sm font-medium capitalize">
                        {entry.entry_type.replace(/_/g, ' ')}
                      </p>
                      <p className="text-muted-foreground text-xs">{formatDate(entry.created_at)}</p>
                      {entry.description && (
                        <p className="text-muted-foreground text-xs mt-0.5">{entry.description}</p>
                      )}
                    </div>
                    <div className="text-right">
                      <p className={`text-sm font-medium ${
                        entry.entry_type.includes('credit') || entry.entry_type.includes('in')
                          ? 'text-emerald-600'
                          : 'text-red-600'
                      }`}>
                        {entry.entry_type.includes('credit') || entry.entry_type.includes('in') ? '+' : '-'}
                        {entry.amount_display} {entry.asset}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </motion.div>
  );
}


// ═════════════════════════════════════════════════════════════════════════════
// MODALS
// ═════════════════════════════════════════════════════════════════════════════

function CreateWalletModal({
  onClose,
  onSuccess,
}: {
  onClose: () => void;
  onSuccess: () => void;
}) {
  const [network, setNetwork] = useState('base');
  const [creating, setCreating] = useState(false);

  const handleCreate = async () => {
    setCreating(true);
    try {
      const res = await cryptoApi.createManagedWallet({ network, purpose: 'primary' });
      if (res.success) {
        toast.success(`Wallet created on ${network}`);
        onSuccess();
      } else {
        toast.error(res.error || 'Failed to create wallet');
      }
    } catch (err: any) {
      toast.error(err.message || 'Failed');
    } finally {
      setCreating(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="bg-card border border-border rounded-2xl p-6 max-w-md w-full"
      >
        <div className="flex items-center justify-between mb-5">
          <h3 className="text-lg font-semibold text-foreground">Create Wallet</h3>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground"><X className="w-5 h-5" /></button>
        </div>

        <p className="text-muted-foreground text-sm mb-4">
          A self-custody wallet will be created for you. Your private key is encrypted and stored securely.
        </p>

        <label className="text-muted-foreground text-sm font-medium mb-2 block">Select Network</label>
        <div className="grid grid-cols-2 gap-2 mb-6">
          {SUPPORTED_NETWORKS.map((n) => (
            <button
              key={n.id}
              onClick={() => setNetwork(n.id)}
              className={`flex items-center gap-2 px-3 py-2.5 rounded-xl border text-sm transition-all ${
                network === n.id
                  ? 'bg-primary/20 border-primary text-foreground'
                  : 'bg-muted/40 border-border text-muted-foreground hover:bg-muted/70 hover:text-foreground'
              }`}
            >
              <span>{n.icon}</span>
              <span>{n.name}</span>
            </button>
          ))}
        </div>

        <button
          onClick={handleCreate}
          disabled={creating}
          className="w-full py-3 rounded-xl bg-primary hover:bg-primary/90 text-primary-foreground font-semibold transition-colors disabled:opacity-50"
        >
          {creating ? <RefreshCw className="w-4 h-4 animate-spin inline mr-2" /> : null}
          {creating ? 'Creating...' : 'Create Wallet'}
        </button>
      </motion.div>
    </div>
  );
}

function RegisterWalletModal({
  onClose,
  onSuccess,
}: {
  onClose: () => void;
  onSuccess: () => void;
}) {
  const [address, setAddress] = useState('');
  const [network, setNetwork] = useState('ethereum');
  const [submitting, setSubmitting] = useState(false);

  const handleRegister = async () => {
    if (!address || !address.startsWith('0x') || address.length !== 42) {
      toast.error('Enter a valid EVM address (0x...)');
      return;
    }
    setSubmitting(true);
    try {
      const res = await cryptoApi.registerExternalWallet({ address, network, purpose: 'primary' });
      if (res.success) {
        toast.success('External wallet registered');
        onSuccess();
      } else {
        toast.error(res.error || 'Failed to register wallet');
      }
    } catch (err: any) {
      toast.error(err.message || 'Failed');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="bg-card border border-border rounded-2xl p-6 max-w-md w-full"
      >
        <div className="flex items-center justify-between mb-5">
          <h3 className="text-lg font-semibold text-foreground">Link External Wallet</h3>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground"><X className="w-5 h-5" /></button>
        </div>

        <p className="text-muted-foreground text-sm mb-4">
          Register an external wallet address you already control (MetaMask, Coinbase, etc).
        </p>

        <div className="space-y-4 mb-6">
          <div>
            <label className="text-muted-foreground text-sm font-medium mb-2 block">Wallet Address</label>
            <input
              type="text"
              value={address}
              onChange={(e) => setAddress(e.target.value)}
              placeholder="0x..."
              className="w-full px-4 py-3 rounded-xl bg-background border border-input text-foreground font-mono text-sm placeholder:text-muted-foreground/60 focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary"
            />
          </div>
          <div>
            <label className="text-muted-foreground text-sm font-medium mb-2 block">Network</label>
            <select
              value={network}
              onChange={(e) => setNetwork(e.target.value)}
              className="w-full px-4 py-3 rounded-xl bg-background border border-input text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary"
            >
              {SUPPORTED_NETWORKS.map((n) => (
                <option key={n.id} value={n.id}>{n.icon} {n.name}</option>
              ))}
            </select>
          </div>
        </div>

        <button
          onClick={handleRegister}
          disabled={submitting || !address}
          className="w-full py-3 rounded-xl bg-primary hover:bg-primary/90 text-primary-foreground font-semibold transition-colors disabled:opacity-50"
        >
          {submitting ? <RefreshCw className="w-4 h-4 animate-spin inline mr-2" /> : null}
          {submitting ? 'Registering...' : 'Register Wallet'}
        </button>
      </motion.div>
    </div>
  );
}
